import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { PageLayout } from '@/components/layout/PageLayout'
import { Separator } from '@/components/shadcn/separator'
import { axiosInstance } from '@/api/axios-instance'
import { notify } from '@/lib/notify'
import {
  useGetApiCompaniesCurrentCompanymanagementusers,
  useGetApiCompaniesCurrentGroups,
  useGetApiCompaniesCurrentActionmodules,
  useGetApiCompaniesCurrentServicemodules,
  useGetApiCompaniesCurrentShopmodules,
  useGetApiCompaniesCurrentSubcompanies,
} from '@/api/generated/company-management-user/company-management-user'
import {
  useGetApiMe,
  useGetApiUsersUserIdPermissions,
  usePostApiUsersUserIdPermissionsEntityTypePermissionName,
  useDeleteApiUsersUserIdPermissionsEntityTypePermissionName,
  usePostApiUsersUserIdPermissionsEntityTypePermissionNameTargetEntityId,
  useDeleteApiUsersUserIdPermissionsEntityTypePermissionNameTargetEntityId,
} from '@/api/generated/user/user'
import { PermissionEntityType } from '@/api/model/permissionEntityType'
import { usePermissions } from '@/hooks/usePermissions'
import {
  type CompanyManagementUser,
  type Permission,
  type SubcategoryItem,
  CATEGORY_ORDER,
} from './permissions.types'
import { UserDialog } from './components/UserDialog'
import { UserListPanel } from './panel-components/UserListPanel'
import { CategoryListPanel } from './panel-components/CategoryListPanel'
import { PermissionListPanel } from './panel-components/PermissionListPanel'
import { PageHeader } from '@/components/layout/PageHeader'
import { shopModuleLabel } from '@/pages/groups/components/ShopModuleIcon'

const toItems = (data: unknown): any[] => {
  const raw = (data as any)?.data ?? data
  return Array.isArray(raw) ? raw : raw?.items ?? []
}

export default function PermissionsPage() {
  // `groups` is needed because shopModuleLabel() reads its type labels from that NS.
  const { t, i18n } = useTranslation(['permissions', 'groups'])
  const { userHasPermission } = usePermissions()
  const canEditPermissions = userHasPermission(PermissionEntityType.Permissions, 'Edit')
  const canCreateCMU = userHasPermission(PermissionEntityType.CompanyManagementUsers, 'Create')
  const canEditCMU = userHasPermission(PermissionEntityType.CompanyManagementUsers, 'Edit')
  const canDeleteCMU = userHasPermission(PermissionEntityType.CompanyManagementUsers, 'Delete')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<PermissionEntityType | null>(null)
  const [userPermissions, setUserPermissions] = useState<Record<string, boolean>>({})
  // Keys with a toggle mutation currently in flight → spinner + locked checkbox in the row.
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set())

  // Dialog state (composition root): open + which user (null = create).
  const [dialog, setDialog] = useState<{ open: boolean; user: CompanyManagementUser | null }>({ open: false, user: null })

  // Management users
  const { data: usersData, isLoading: usersLoading, isError: usersError } = useGetApiCompaniesCurrentCompanymanagementusers()
  const users: CompanyManagementUser[] = useMemo(() => toItems(usersData), [usersData])

  // Own identity — you may not change your own permissions (BON-1852).
  const { data: meData } = useGetApiMe()
  const isSelf = !!selectedUserId && selectedUserId === meData?.data?.id

  // Permission definitions (no generated hook)
  const { data: definitionsData } = useQuery({
    queryKey: ['permissionDefinitions'],
    queryFn: () => axiosInstance<Permission[]>({ url: '/api/permissions/definitions', method: 'GET' }).then(r => r.data),
  })
  const definitions: Permission[] = Array.isArray(definitionsData) ? definitionsData : []

  // Object data per object category
  const { data: groupsData } = useGetApiCompaniesCurrentGroups()
  const { data: actionModulesData } = useGetApiCompaniesCurrentActionmodules()
  const { data: serviceModulesData } = useGetApiCompaniesCurrentServicemodules()
  const { data: shopModulesData } = useGetApiCompaniesCurrentShopmodules()
  const { data: subCompaniesData } = useGetApiCompaniesCurrentSubcompanies()

  const entityItemsMap: Partial<Record<PermissionEntityType, SubcategoryItem[]>> = useMemo(() => {
    // i18n.resolvedLanguage is not read here, but it keeps the memo honest: the shop module
    // names below are translated labels, so a language switch must recompute.
    void i18n.resolvedLanguage
    return {
      [PermissionEntityType.Groups]: toItems(groupsData),
      [PermissionEntityType.ActionModules]: toItems(actionModulesData),
      [PermissionEntityType.ServiceModules]: toItems(serviceModulesData),
      // Shop modules carry no name — the section heading is the translated type label.
      [PermissionEntityType.ShopModules]: toItems(shopModulesData).map(m => ({ id: m.id, name: shopModuleLabel(m.type) })),
      [PermissionEntityType.SubCompanies]: toItems(subCompaniesData),
    }
  }, [groupsData, actionModulesData, serviceModulesData, shopModulesData, subCompaniesData, i18n.resolvedLanguage])

  // Permissions of the selected user
  const {
    data: permissionsData,
    refetch: refetchPermissions,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useGetApiUsersUserIdPermissions(
    selectedUserId ?? '', {},
    { query: { enabled: !!selectedUserId } }
  )

  useEffect(() => {
    if (!permissionsData) return
    const raw = (permissionsData as any)?.data ?? permissionsData
    const perms: Permission[] = Array.isArray(raw) ? raw : raw?.items ?? raw?.permissions ?? []
    const map: Record<string, boolean> = {}
    perms.forEach((p) => {
      const key = p.targetEntityId != null
        ? `${p.entityType}.${p.permissionName}.${p.targetEntityId}`
        : `${p.entityType}.${p.permissionName}`
      map[key] = true
    })
    setUserPermissions(map)
  }, [permissionsData])

  // Permissions per entityType (== category): global vs. per-object.
  // Deduplicated by (entityType, permissionName, isPerEntity): the seeder only skips *new*
  // duplicates, so a definition row that already exists twice in the DB would otherwise
  // render the same checkbox twice (and collide on the row key).
  const categorizedPermissions = useMemo(() => {
    const seen = new Set<string>()
    return definitions.reduce<Partial<Record<PermissionEntityType, { global: Permission[]; perEntity: Permission[] }>>>((acc, perm) => {
      const et = perm.entityType
      const dedupeKey = `${et}.${perm.permissionName}.${perm.isPerEntity ? 'entity' : 'global'}`
      if (seen.has(dedupeKey)) return acc
      seen.add(dedupeKey)
      if (!acc[et]) acc[et] = { global: [], perEntity: [] }
      if (perm.isPerEntity) acc[et]!.perEntity.push(perm)
      else acc[et]!.global.push(perm)
      return acc
    }, {})
  }, [definitions])

  // Only show entityTypes that actually have permission definitions.
  const categories = useMemo(
    () => CATEGORY_ORDER.filter(et => categorizedPermissions[et]),
    [categorizedPermissions]
  )

  // No user is preselected on purpose: the page would otherwise open on somebody's rights
  // without anyone having picked him, which invites a toggle on the wrong account. The category
  // keeps its preselection — it carries no account.
  useEffect(() => {
    if (!selectedCategory && categories.length > 0) setSelectedCategory(categories[0])
  }, [categories, selectedCategory])

  // Permission mutations (global + per-object)
  const addGeneral = usePostApiUsersUserIdPermissionsEntityTypePermissionName()
  const removeGeneral = useDeleteApiUsersUserIdPermissionsEntityTypePermissionName()
  const addEntity = usePostApiUsersUserIdPermissionsEntityTypePermissionNameTargetEntityId()
  const removeEntity = useDeleteApiUsersUserIdPermissionsEntityTypePermissionNameTargetEntityId()

  // Toggle optimistically, roll back on error.
  async function handleToggle(perm: Permission, targetEntityId?: number) {
    if (!selectedUserId) return
    // You may not revoke your own permission management (BON-1852).
    if (isSelf && perm.entityType === PermissionEntityType.Permissions) return
    const key = targetEntityId != null
      ? `${perm.entityType}.${perm.permissionName}.${targetEntityId}`
      : `${perm.entityType}.${perm.permissionName}`
    const current = userPermissions[key] ?? false
    // Ignore double-clicks while this row's mutation is still running.
    if (pendingKeys.has(key)) return

    setUserPermissions(prev => ({ ...prev, [key]: !current }))
    setPendingKeys(prev => new Set(prev).add(key))

    try {
      if (targetEntityId != null) {
        if (current) {
          await removeEntity.mutateAsync({ userId: selectedUserId, entityType: perm.entityType, permissionName: perm.permissionName, targetEntityId })
        } else {
          await addEntity.mutateAsync({ userId: selectedUserId, entityType: perm.entityType, permissionName: perm.permissionName, targetEntityId })
        }
      } else {
        if (current) {
          await removeGeneral.mutateAsync({ userId: selectedUserId, entityType: perm.entityType, permissionName: perm.permissionName })
        } else {
          await addGeneral.mutateAsync({ userId: selectedUserId, entityType: perm.entityType, permissionName: perm.permissionName })
        }
      }
      refetchPermissions()
    } catch (error) {
      // Roll back the optimistic update + report the error.
      setUserPermissions(prev => ({ ...prev, [key]: current }))
      notify.apiError(error, t('toasts.permissionError'))
    } finally {
      setPendingKeys(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const permsForCategory = selectedCategory ? categorizedPermissions[selectedCategory] : undefined
  const itemsForCategory = selectedCategory ? entityItemsMap[selectedCategory] ?? [] : []

  return (
    <PageLayout>
      <PageHeader title={t('header.title')} />

      <div className="flex w-full gap-0 border rounded-lg overflow-hidden flex-1 min-h-[500px]">
        <UserListPanel
          users={users}
          isLoading={usersLoading}
          isError={usersError}
          selectedUserId={selectedUserId}
          onSelectUser={(user) => setSelectedUserId(user.id)}
          onAddUser={() => setDialog({ open: true, user: null })}
          onEditUser={(user) => setDialog({ open: true, user })}
          canCreate={canCreateCMU}
          canEdit={canEditCMU}
        />

        <Separator orientation="vertical" />

        <CategoryListPanel
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />

        <Separator orientation="vertical" />

        <PermissionListPanel
          selectedUserId={selectedUserId}
          selectedCategory={selectedCategory}
          permissions={permsForCategory}
          items={itemsForCategory}
          userPermissions={userPermissions}
          pendingKeys={pendingKeys}
          isLoading={permissionsLoading}
          isError={permissionsError}
          onToggle={handleToggle}
          isSelf={isSelf}
          canEdit={canEditPermissions}
        />
      </div>

      <UserDialog
        isOpen={dialog.open}
        onOpenChange={(open) => setDialog(prev => ({ ...prev, open }))}
        user={dialog.user}
        canDelete={canDeleteCMU}
        onDeleted={(userId) => { if (selectedUserId === userId) setSelectedUserId(null) }}
      />
    </PageLayout>
  )
}
